import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "@store/store";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import { setDialogConfig, setPopupConfig } from "@store/slices/componentsSlice";
import { useLoadApplicationSelected } from "@hooks/useLoadApplicationSelected";
import { Loader } from "@components/Loader";

export default function Solicitud() {
    const params = useParams();
    const navigation = useNavigate();
    const dispatch = useDispatch();

    const sectionSelected = useSelector((state: RootState) => state.components.currentSection.section);
    const reloadSection = useSelector((state: RootState) => state.components.currentSection.reload);

    const { loadApplicationSelected } = useLoadApplicationSelected();

    // Llave (empresa + folio) de la última solicitud consultada en este montaje del shell.
    // Inicia vacía para que al recargar la página (F5) siempre se refresque la información
    // aunque el store rehidratado de sessionStorage ya tenga la misma solicitud.
    const loadedKey = useRef<string | null>(null);
    // Las secciones se muestran hasta tener la información de la solicitud de la URL, para que no
    // consulten con la solicitud anterior o con una fila de la lista guardada en el store
    const [isApplicationReady, setIsApplicationReady] = useState(false);

    const companySource = params.companyName?.toUpperCase() ?? "";
    const applicationKey = `${companySource}_${params.id}`;

    const loadApplicationData = async (forceTaxSystem: boolean = false) => {
        try {
            const application = await loadApplicationSelected({
                companyId: Number(params.id),
                companySource: companySource,
                forceTaxSystem: forceTaxSystem,
            });

            if (application == undefined) {
                navigation("/not_found");
            }
        } catch (error: any) {
            if (error.status == 401) {
                navigation("/");
            }
        }
    };

    useEffect(() => {
        dispatch(setPopupConfig());
        dispatch(setDialogConfig());
    }, [sectionSelected]);

    useEffect(() => {
        if (loadedKey.current === applicationKey) return;

        loadedKey.current = applicationKey;
        setIsApplicationReady(false);
        loadApplicationData().finally(() => {
            if (loadedKey.current === applicationKey) setIsApplicationReady(true);
        });
    }, [applicationKey]);

    // Botón "Recargar": se vuelve a consultar el resumen y el régimen fiscal.
    // El flag lo regresan a false las secciones al terminar su propia recarga.
    useEffect(() => {
        if (reloadSection && loadedKey.current === applicationKey) {
            loadApplicationData(true);
        }
    }, [reloadSection]);

    if (!isApplicationReady) return <Loader />;

    return <Outlet />;
}
